// Range slider for the diameter list filter (custom.diameter_o_mm).
// The native Search & Discovery checkboxes stay in the form (hidden); the slider
// checks every value inside the chosen range and lets facet-form submit as usual.
class DiameterRange extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.unit = this.dataset.unit || '';
    this.wrapper = this.querySelector('.facet-range-wrapper');
    this.minRangeInput = this.querySelector('.facet-range__min');
    this.maxRangeInput = this.querySelector('.facet-range__max');
    this.label = this.querySelector('.diameter-range__label');

    this.items = Array.from(this.querySelectorAll('input[type="checkbox"]')).map((checkbox) => ({
      checkbox,
      num: parseFloat(checkbox.value.replace(',', '.')),
    }));

    // Only values that can be submitted form the scale, so any range contains at least one of them.
    this.steps = [
      ...new Set(this.items.filter((item) => !item.checkbox.disabled && !isNaN(item.num)).map((item) => item.num)),
    ].sort((a, b) => a - b);
    this.last = this.steps.length - 1;

    if (this.steps.length < 2) {
      this.wrapper.hidden = true;
      this.label.textContent = this.steps.length ? this.format(0, 0) : '';
      return;
    }

    const checkedNums = this.items.filter((item) => item.checkbox.checked && !isNaN(item.num)).map((item) => item.num);
    const lo = checkedNums.length ? this.steps.indexOf(Math.min(...checkedNums)) : 0;
    const hi = checkedNums.length ? this.steps.indexOf(Math.max(...checkedNums)) : this.last;

    [this.minRangeInput, this.maxRangeInput].forEach((input) => {
      input.max = this.last;
    });
    this.committed = { lo: Math.max(lo, 0), hi: hi < 0 ? this.last : hi };
    this.setPosition(this.committed.lo, this.committed.hi);

    this.minRangeInput.addEventListener('input', this.onInput.bind(this));
    this.maxRangeInput.addEventListener('input', this.onInput.bind(this));
    this.minRangeInput.addEventListener('change', this.onChange.bind(this));
    this.maxRangeInput.addEventListener('change', this.onChange.bind(this));
  }

  format(lo, hi) {
    const range = lo === hi ? `${this.steps[lo]}` : `${this.steps[lo]} – ${this.steps[hi]}`;
    return this.unit ? `${range} ${this.unit}` : range;
  }

  setPosition(lo, hi) {
    this.minRangeInput.value = lo;
    this.maxRangeInput.value = hi;
    this.wrapper.style.setProperty('--range-min', `${(lo / this.last) * 100}%`);
    this.wrapper.style.setProperty('--range-max', `${(hi / this.last) * 100}%`);
    this.label.textContent = this.format(lo, hi);
  }

  onInput(event) {
    let lo = parseInt(this.minRangeInput.value);
    let hi = parseInt(this.maxRangeInput.value);

    if (lo > hi) {
      if (event.target === this.minRangeInput) lo = hi;
      else hi = lo;
    }

    this.setPosition(lo, hi);
  }

  onChange(event) {
    event.stopPropagation();

    const lo = parseInt(this.minRangeInput.value);
    const hi = parseInt(this.maxRangeInput.value);
    const isFullRange = lo === 0 && hi === this.last;
    const min = this.steps[lo];
    const max = this.steps[hi];

    // Full range means "no diameter filter", so products without a diameter stay visible.
    const nextChecked = this.items.map(
      ({ checkbox, num }) => !isFullRange && !checkbox.disabled && !isNaN(num) && num >= min && num <= max
    );

    if (!isFullRange && !nextChecked.includes(true)) {
      this.setPosition(this.committed.lo, this.committed.hi);
      return;
    }

    const changed = this.items.some(({ checkbox }, index) => checkbox.checked !== nextChecked[index]);
    this.committed = { lo, hi };
    if (!changed) return;

    this.items.forEach(({ checkbox }, index) => {
      checkbox.checked = nextChecked[index];
    });
    this.items[0].checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

if (!customElements.get('diameter-range')) customElements.define('diameter-range', DiameterRange);
